import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';

const LABEL_MAP = { paperType: 'Paper Type', printSize: 'Print Size' };
const ARTIST_PROOFS_PRODUCT_TYPE = 'artist_proofs';

const stripPrice = (text) => text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();

const filterPaperOption = (option) => {
  if (!option?.text) return false;
  const t = option.text.toLowerCase();
  return !t.includes('canvas') && !t.includes('lustre') && !t.includes('deckle');
};

const ArtistProofsFormInputs = ({ quantityRef }) => {
  const { form, setForm, processPrintSizeData, processEditingSoftwareData } =
    useProductSelectionStore();
  const paperTypeSelectedValue = form?.inputs?.paperType?.selectedValue;

  useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (printSizes) =>
      processPrintSizeData(
        printSizes.filter((printSize) => printSize.productType === ARTIST_PROOFS_PRODUCT_TYPE),
      ),
  });

  const editingSoftwareQuery = useQuery({
    queryKey: ['editingSoftwareOptions', paperTypeSelectedValue],
    queryFn: () => httpClient.get('products/editing_software_options'),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: processEditingSoftwareData,
  });

  useEffect(() => {
    const printSizeOptions = form?.inputs?.printSize?.options;
    if (!printSizeOptions?.length) return;

    const option5x7 = printSizeOptions.find((opt) =>
      opt.text?.replace(/\s/g, '').includes('5x7'));
    const option5x7Price = Number(option5x7?.price);
    const option5x7Text = option5x7
      ? `${stripPrice(option5x7.text)} ($${option5x7Price.toFixed(2)})`
      : undefined;
    const currentSelectedValue = form?.inputs?.printSize?.selectedValue;

    const needs5x7Select = option5x7 && currentSelectedValue !== option5x7.value;
    const needsDisable = !form?.inputs?.printSize?.isDisabled;
    const needsPriceUpdate =
      option5x7 &&
      (Number(form?.inputs?.printSize?.selectedValuePrice) !== option5x7Price ||
        form?.inputs?.printSize?.selectedValueText !== option5x7Text);
    const option5x7Width = Number(option5x7?.xAspectRatio);
    const option5x7Height = Number(option5x7?.yAspectRatio);
    const hasOption5x7Dimensions = option5x7Width > 0 && option5x7Height > 0;
    const option5x7AspectRatio = option5x7Width / option5x7Height;
    const currentWidth = Number(form?.meta?.xAspectRatio);
    const currentHeight = Number(form?.meta?.yAspectRatio);
    const hasValid5x7Orientation =
      (currentWidth === option5x7Width && currentHeight === option5x7Height) ||
      (currentWidth === option5x7Height && currentHeight === option5x7Width);
    const needsAspectRatio =
      hasOption5x7Dimensions && !hasValid5x7Orientation;

    if (!needs5x7Select && !needsDisable && !needsPriceUpdate && !needsAspectRatio) return;

    setForm((prev) => ({
      ...prev,
      meta: {
        ...prev.meta,
        ...(needsAspectRatio && {
          xAspectRatio: option5x7.xAspectRatio,
          yAspectRatio: option5x7.yAspectRatio,
          aspectRatio: option5x7AspectRatio,
        }),
      },
      inputs: {
        ...prev.inputs,
        printSize: {
          ...prev.inputs.printSize,
          options: option5x7
            ? [{ ...option5x7, text: option5x7Text, price: option5x7Price }]
            : [],
          isDisabled: true,
          ...(option5x7 && {
            selectedValue: option5x7.value,
            selectedValueText: option5x7Text,
            selectedValuePrice: option5x7Price,
          }),
        },
      },
    }));
  }, [
    form?.inputs?.printSize?.options,
    form?.inputs?.printSize?.selectedValue,
    form?.meta?.xAspectRatio,
    form?.meta?.yAspectRatio,
    setForm,
  ]);

  useEffect(() => {
    if (editingSoftwareQuery.data) processEditingSoftwareData(editingSoftwareQuery.data);
  }, [editingSoftwareQuery.data, processEditingSoftwareData]);

  useEffect(() => {
    const paperTypeValue = form?.inputs?.paperType?.selectedValue;
    const printSizeValue = form?.inputs?.printSize?.selectedValue;
    const editingSoftwareValue = form?.inputs?.editingSoftware?.selectedValue;
    const missing = [
      ...(!paperTypeValue || paperTypeValue === 'default' ? ['Paper Type'] : []),
      ...(!printSizeValue || printSizeValue === 'default' ? ['Print Size'] : []),
      ...(!editingSoftwareValue || editingSoftwareValue === 'default' ? ['Editing Software'] : []),
    ];

    setForm((prev) => {
      const current = prev?.meta?.requiredInputs ?? [];
      if (current.length === missing.length && current.every((v, i) => v === missing[i])) {
        return prev;
      }
      return {
        ...prev,
        meta: {
          ...prev.meta,
          requiredInputs: missing,
        },
      };
    });
  }, [
    form?.inputs?.paperType?.selectedValue,
    form?.inputs?.printSize?.selectedValue,
    form?.inputs?.editingSoftware?.selectedValue,
    form?.meta?.requiredInputs,
    setForm,
  ]);

  const handleInputChange = (key, e) => {
    const { value } = e.target;
    const selectedText = e.target.options[e.target.selectedIndex].text;
    setForm((prev) => {
      const resetInputs = { ...prev.inputs };
      if (key === 'paperType') {
        resetInputs.printSize = {
          ...resetInputs.printSize,
          selectedValue: undefined,
          selectedValueText: undefined,
          selectedValuePrice: undefined,
        };
        resetInputs.editingSoftware = {
          ...resetInputs.editingSoftware,
          options: [],
          selectedValue: 'default',
          selectedValueText: undefined,
          isLoading: value !== 'default',
          isDisabled: true,
        };
      }
      const nextMeta = { ...prev.meta };
      if (key === 'paperType') {
        nextMeta.xAspectRatio = undefined;
        nextMeta.yAspectRatio = undefined;
        nextMeta.aspectRatio = undefined;
      }
      return {
        ...prev,
        meta: nextMeta,
        inputs: {
          ...resetInputs,
          [key]: { ...resetInputs[key], selectedValue: value, selectedValueText: selectedText },
        },
      };
    });
  };

  if (!form?.inputs) return null;

  return (
    <div className={styles.printsFormStack}>
      <div className="input-container quantity">
        <div className="label-container">
          <div className="div-block-150">
            <div className="sub-label-form">Quantity</div>
          </div>
        </div>
        <div className="form-block-input w-form">
          <div className="input-wrapper">
            <input
              ref={quantityRef}
              type="number"
              min="1"
              maxLength="256"
              name="field-quantity"
              defaultValue="1"
              className="text-field-2 data-jscolor w-input"
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {Object.keys(form.inputs).map((key) => {
        const input = form.inputs[key];
        if (key === 'finishingService' || !input) return null;

        return (
          <div key={key} className="input-container paper-type">
            <div className="label-container">
              <div className="div-block-150">
                <div className="sub-label-form">{input.label || LABEL_MAP[key] || key}</div>
              </div>
            </div>
            <div className="form-block-input w-form">
              <form className="form-input select-field">
                <select
                  className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                  style={{ width: '100%' }}
                  onChange={(e) => handleInputChange(key, e)}
                  value={input.selectedValue || 'default'}
                  disabled={input.isDisabled}
                >
                  {input.isLoading && !input.options?.length ? (
                    <option value="">Loading...</option>
                  ) : (
                    <option value="default">Choose One</option>
                  )}
                  {(input.options || [])
                    .filter((option) => {
                      if (!option?.text || option.value === 'default') return false;
                      return key === 'paperType' ? filterPaperOption(option) : true;
                    })
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {key === 'paperType' ? stripPrice(option.text) : option.text}
                      </option>
                    ))}
                </select>
                <img
                  src={ChevronIcon}
                  alt=""
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    width: '16px',
                    height: '16px',
                  }}
                />
              </form>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ArtistProofsFormInputs;
